import { useCallback } from "react";

const useFileContentInteractions = ({
  activeFile,
  files,
  activeFileContent,
  setActiveFileContent,
  setSelectedFileLines,
  inputRef,
}) => {
  const handleFileLineSelection = useCallback((fileName, lineIndex, isSelected) => {
    const findRealFileKey = (simpleFileName) => {
      if (
        activeFile &&
        (activeFile === simpleFileName ||
          activeFile.includes(`/${simpleFileName}`) ||
          activeFile.split("/").pop() === simpleFileName)
      ) {
        return activeFile;
      }

      if (files[simpleFileName]) {
        return simpleFileName;
      }

      const pureName = simpleFileName.split("/").pop();

      const exactMatches = Object.keys(files).filter(
        (key) => key.split("/").pop() === pureName
      );

      if (exactMatches.length > 0) {
        if (activeFile && exactMatches.includes(activeFile)) {
          return activeFile;
        }
        return exactMatches.sort().pop();
      }

      const possibleKeys = Object.keys(files).filter(
        (key) =>
          key.includes(simpleFileName) ||
          key.includes(simpleFileName.replace(".md", ""))
      );

      if (possibleKeys.length > 0) {
        return possibleKeys[0];
      }

      return null;
    };

    const realFileKey = findRealFileKey(fileName);

    const getFileContent = (name) => {
      const fileKey = realFileKey || name;

      if (
        activeFileContent &&
        (activeFileContent.fileName === name ||
          activeFileContent.fileName === fileKey) &&
        activeFileContent.content
      ) {
        return activeFileContent.content;
      }

      if (realFileKey && files[realFileKey]) {
        if (typeof files[realFileKey].content === "string") {
          return files[realFileKey].content;
        }
        if (
          files[realFileKey].content &&
          typeof files[realFileKey].content === "object"
        ) {
          if ("content" in files[realFileKey].content) {
            return files[realFileKey].content.content || "";
          }
          return JSON.stringify(files[realFileKey].content, null, 2);
        }
        return String(files[realFileKey].content || "");
      }

      return "";
    };

    if (isSelected) {
      try {
        const fileContent = getFileContent(fileName);

        if (fileContent) {
          setActiveFileContent({
            fileName: realFileKey || fileName,
            content: fileContent,
          });

          if (!window._fileContentCache) window._fileContentCache = {};
          window._fileContentCache[fileName] = {
            realKey: realFileKey,
            content: fileContent,
            lines: fileContent.split("\n"),
            timestamp: Date.now(),
          };

          const lines = fileContent.split("\n");
          if (lineIndex >= 0 && lineIndex < lines.length) {
            window._selectedLineContent = lines[lineIndex];
          }
        }
      } catch (error) {
        console.error("处理文件内容时出错:", error);
      }
    }

    setSelectedFileLines((prev) => {
      const fileLines = prev[fileName] || [];
      const updatedLines = isSelected
        ? [...fileLines, lineIndex]
        : fileLines.filter((i) => i !== lineIndex);

      if (updatedLines.length === 0) {
        const newState = { ...prev };
        delete newState[fileName];
        return newState;
      }

      return {
        ...prev,
        [fileName]: updatedLines,
      };
    });

    if (isSelected && inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    activeFile,
    activeFileContent,
    files,
    inputRef,
    setActiveFileContent,
    setSelectedFileLines,
  ]);

  const fallbackCopyToClipboard = useCallback((text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;

      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      if (successful) {
        console.log("使用备用方法复制成功");
      } else {
        console.error("备用复制方法失败");
      }

      document.body.removeChild(textArea);
    } catch (err) {
      console.error("复制到剪贴板失败:", err);
    }
  }, []);

  const copyToClipboard = useCallback(
    (text) => {
      if (!text) return;

      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        navigator.clipboard.writeText(text).catch((err) => {
          console.error("复制失败(clipboard API):", err);
          fallbackCopyToClipboard(text);
        });
      } else {
        fallbackCopyToClipboard(text);
      }
    },
    [fallbackCopyToClipboard]
  );

  return {
    handleFileLineSelection,
    copyToClipboard,
  };
};

export default useFileContentInteractions;
